<template>
  <div
    class="relative flex items-start min-h-screen w-full max-w-full overflow-x-clip bg-slate-50 dark:bg-slate-900"
  >
    <!-- Sidebar -->
    <AdminSidebar
      :is-collapsed="isSidebarCollapsed"
      :is-open-mobile="isSidebarOpenMobile"
      :sidebar-search="sidebarSearch"
      :lang="lang"
      :t="tr"
      @update:sidebar-search="sidebarSearch = $event"
    />


    <!-- Main content -->
    <div class="flex flex-col flex-1 min-w-0 w-full">
      <!-- Top bar -->
      <AdminTopbar
        :is-collapsed="isSidebarCollapsed"
        :lang="lang"
        :t="tr"
        @toggle-sidebar="toggleSidebar"
      />

      <!-- Content area -->
      <main class="flex-1 px-4 py-6 md:px-6 lg:px-8">
        <div class="max-w-6xl mx-auto">
          <!-- Page header -->
          <div
            class="flex flex-col justify-between gap-3 mb-4 sm:flex-row sm:items-center"
          >
            <div>
              <h1 class="text-lg font-semibold text-slate-900 dark:text-white">
                {{ tr("office_title") }}
              </h1>
              <p class="text-sm text-slate-500 dark:text-slate-400">
                {{ tr("office_subtitle") }}
              </p>
            </div>
          </div>

          <!-- Card wrapper -->
          <section
            class="overflow-hidden bg-white border shadow-sm dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded-2xl"
          >
            <!-- Card header -->
            <header
              class="flex items-center gap-2 px-5 py-3 text-sm font-medium border-b bg-slate-100/80 dark:bg-slate-800/80 border-slate-200 dark:border-slate-800"
            >
              <i
                class="mdi mdi-office-building-marker-outline text-secondary"
              ></i>
              <span>{{ tr("office_section_info") }}</span>
            </header>

            <form @submit.prevent="saveOfficeInfo">
              <!-- Office info fields -->
              <div class="px-5 pt-5 pb-4 space-y-4">
                <div
                  class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
                >
                  <!-- CRN -->
                  <div>
                    <label
                      class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                    >
                      {{ tr("office_field_crn") }}
                      <span class="text-[10px] text-slate-400">{{
                        tr("office_optional")
                      }}</span>
                    </label>
                    <div class="relative">
                      <span
                        class="absolute inset-y-0 flex items-center justify-center w-9 bg-secondary/15 text-primary"
                        :class="
                          lang === 'ar'
                            ? 'right-0 rounded-r-md'
                            : 'left-0 rounded-l-md'
                        "
                      >
                        <i class="mdi mdi-identifier text-[18px]"></i>
                      </span>
                      <input
                        v-model="officeForm.crn"
                        type="text"
                        class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-primary/40"
                        :class="lang === 'ar' ? 'pr-10 pl-3' : 'pl-10 pr-3'"
                        :placeholder="tr('office_field_crn_placeholder')"
                      />
                    </div>
                  </div>

                  <!-- Establishment -->
                  <div>
                    <label
                      class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                    >
                      {{ tr("office_field_establishment") }}
                      <span class="text-[10px] text-slate-400">{{
                        tr("office_optional")
                      }}</span>
                    </label>
                    <div class="relative">
                      <span
                        class="absolute inset-y-0 flex items-center justify-center w-9 bg-secondary/15 text-primary"
                        :class="
                          lang === 'ar'
                            ? 'right-0 rounded-r-md'
                            : 'left-0 rounded-l-md'
                        "
                      >
                        <i
                          class="mdi mdi-card-account-details-outline text-[18px]"
                        ></i>
                      </span>
                      <input
                        v-model="officeForm.establishment_no"
                        type="text"
                        class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-primary/40"
                        :class="lang === 'ar' ? 'pr-10 pl-3' : 'pl-10 pr-3'"
                        :placeholder="
                          tr('office_field_establishment_placeholder')
                        "
                      />
                    </div>
                  </div>

                  <!-- Unified -->
                  <div>
                    <label
                      class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                    >
                      {{ tr("office_field_unified_no") }}
                      <span class="text-[10px] text-slate-400">{{
                        tr("office_optional")
                      }}</span>
                    </label>
                    <div class="relative">
                      <span
                        class="absolute inset-y-0 flex items-center justify-center w-9 bg-secondary/15 text-primary"
                        :class="
                          lang === 'ar'
                            ? 'right-0 rounded-r-md'
                            : 'left-0 rounded-l-md'
                        "
                      >
                        <span class="text-[13px] font-semibold">#</span>
                      </span>
                      <input
                        v-model="officeForm.unified_no"
                        type="text"
                        class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-primary/40"
                        :class="lang === 'ar' ? 'pr-10 pl-3' : 'pl-10 pr-3'"
                        :placeholder="tr('office_field_unified_no_placeholder')"
                      />
                    </div>
                  </div>

                  <!-- Tax -->
                  <div>
                    <label
                      class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                    >
                      {{ tr("office_field_tax_no") }}
                      <span class="text-[10px] text-slate-400">{{
                        tr("office_optional")
                      }}</span>
                    </label>
                    <div class="relative">
                      <span
                        class="absolute inset-y-0 flex items-center justify-center w-9 bg-secondary/15 text-primary"
                        :class="
                          lang === 'ar'
                            ? 'right-0 rounded-r-md'
                            : 'left-0 rounded-l-md'
                        "
                      >
                        <i class="mdi mdi-receipt-outline text-[18px]"></i>
                      </span>
                      <input
                        v-model="officeForm.tax_no"
                        type="text"
                        class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-primary/40"
                        :class="lang === 'ar' ? 'pr-10 pl-3' : 'pl-10 pr-3'"
                        :placeholder="tr('office_field_tax_no_placeholder')"
                      />
                    </div>
                  </div>

                  <!-- Insurance -->
                  <div>
                    <label
                      class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                    >
                      {{ tr("office_field_insurance_no") }}
                      <span class="text-[10px] text-slate-400">{{
                        tr("office_optional")
                      }}</span>
                    </label>
                    <div class="relative">
                      <span
                        class="absolute inset-y-0 flex items-center justify-center w-9 bg-secondary/15 text-primary"
                        :class="
                          lang === 'ar'
                            ? 'right-0 rounded-r-md'
                            : 'left-0 rounded-l-md'
                        "
                      >
                        <i class="mdi mdi-shield-check-outline text-[18px]"></i>
                      </span>
                      <input
                        v-model="officeForm.insurance_no"
                        type="text"
                        class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-primary/40"
                        :class="lang === 'ar' ? 'pr-10 pl-3' : 'pl-10 pr-3'"
                        :placeholder="
                          tr('office_field_insurance_no_placeholder')
                        "
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Upload header -->
              <div
                class="flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-y bg-slate-100/80 dark:bg-slate-800/80 border-slate-200 dark:border-slate-800"
              >
                <i class="mdi mdi-paperclip text-secondary"></i>
                <span>{{ tr("office_section_upload") }}</span>
              </div>

              <!-- Upload area -->
              <div class="px-5 py-4 space-y-5">
                <!-- Choose doc type -->
                <div>
                  <h3
                    class="mb-2 text-xs font-bold tracking-wide text-slate-600 dark:text-slate-300"
                  >
                    {{ tr("office_choose_doc_type") }}
                  </h3>

                  <div
                    class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3"
                  >
                    <label
                      v-for="doc in docTypes"
                      :key="doc.key"
                      class="flex items-center gap-2 p-2 text-xs border cursor-pointer rounded-xl bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:border-primary/60"
                      :class="
                        selectedDocType === doc.key
                          ? 'ring-2 ring-primary/30 border-primary/60 bg-primary/5 dark:bg-primary/10'
                          : ''
                      "
                    >
                      <input
                        type="radio"
                        name="docType"
                        :value="doc.key"
                        v-model="selectedDocType"
                      />
                      <span class="truncate text-slate-700 dark:text-slate-200">
                        {{ tr(doc.labelKey) }}
                      </span>
                    </label>
                  </div>
                </div>

                <!-- Upload picker -->
                <div class="space-y-2">
                  <label
                    class="block text-xs font-medium text-slate-600 dark:text-slate-300"
                  >
                    {{ tr(selectedDocMeta.labelKey) }}
                    <span class="text-[10px] text-slate-400">{{
                      tr("office_optional")
                    }}</span>
                  </label>

                  <div class="relative">
                    <span
                      class="absolute inset-y-0 flex items-center justify-center w-9 bg-secondary/15 text-primary"
                      :class="
                        lang === 'ar'
                          ? 'right-0 rounded-r-md'
                          : 'left-0 rounded-l-md'
                      "
                    >
                      <i class="mdi mdi-paperclip text-[18px]"></i>
                    </span>

                    <input
                      type="text"
                      class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-300 focus:outline-none focus:ring-1 focus:ring-primary/40"
                      :class="lang === 'ar' ? 'pr-10 pl-3' : 'pl-10 pr-3'"
                      :placeholder="tr(selectedDocMeta.placeholderKey)"
                      :value="uploadedDocNames[selectedDocType.value] || ''"
                      readonly
                      @click="triggerFilePicker"
                    />

                    <input
                      ref="singleFileInput"
                      type="file"
                      :accept="selectedDocMeta.accept || '*/*'"
                      class="hidden"
                      @change="handleSelectedDocFile"
                    />
                  </div>

                  <p class="text-[11px] text-slate-400">
                    {{ tr("office_upload_hint") }}
                  </p>
                </div>

                <!-- Selected uploads preview -->
                <div v-if="hasAnyUploaded" class="space-y-2">
                  <h4
                    class="text-xs font-semibold text-slate-600 dark:text-slate-300"
                  >
                    {{ tr("office_selected_uploads") }}
                  </h4>

                  <div
                    class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3"
                  >
                    <div
                      v-for="doc in docTypes"
                      :key="doc.key"
                      class="flex items-center justify-between gap-2 p-3 text-xs border rounded-xl bg-slate-50 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700"
                      :class="!uploadedDocs[doc.key] ? 'opacity-60' : ''"
                    >
                      <div class="min-w-0">
                        <div
                          class="font-semibold truncate text-slate-700 dark:text-slate-200"
                        >
                          {{ tr(doc.labelKey) }}
                        </div>
                        <div
                          class="truncate text-slate-500 dark:text-slate-400"
                        >
                          {{
                            uploadedDocNames[doc.key] || tr("office_no_file")
                          }}
                        </div>
                      </div>

                      <div class="flex items-center gap-1">
                        <button
                          v-if="uploadedDocs[doc.key]"
                          type="button"
                          class="px-2 py-1 text-[11px] rounded-md bg-primary/10 text-primary hover:bg-primary/20"
                          @click="selectTypeAndPick(doc.key)"
                        >
                          {{ tr("office_change") }}
                        </button>

                        <button
                          v-if="uploadedDocs[doc.key]"
                          type="button"
                          class="px-2 py-1 text-[11px] rounded-md bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-500/10 dark:text-rose-300"
                          @click="removeUploaded(doc.key)"
                        >
                          {{ tr("office_remove") }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Existing files -->
                <div v-if="existingFilesList.length" class="mt-6">
                  <h3
                    class="mb-3 text-xs font-bold tracking-wide text-slate-600 dark:text-slate-300"
                  >
                    {{ tr("office_existing_files_title") }}
                  </h3>

                  <div
                    class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
                  >
                    <DocCard
                      v-for="item in existingFilesList"
                      :key="item.key"
                      :label="tr(item.labelKey)"
                      :url="item.url"
                      @open="openExistingFile(item.key)"
                    />
                  </div>
                </div>
              </div>

              <!-- Footer actions -->
              <footer
                class="flex flex-col items-start justify-between gap-3 px-5 py-4 border-t bg-slate-50/80 dark:bg-slate-900/70 border-slate-200 dark:border-slate-800 sm:flex-row sm:items-center"
              >
                <div class="flex flex-wrap gap-2">
                  <button
                    type="submit"
                    class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white rounded-lg shadow-sm bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/60"
                    :disabled="submitting"
                  >
                    <i
                      v-if="submitting"
                      class="mdi mdi-loading mdi-spin text-[18px]"
                      :class="lang === 'ar' ? 'ml-1' : 'mr-1'"
                    ></i>
                    {{ tr("office_btn_save") }}
                  </button>

                  <button
                    type="button"
                    @click="reloadOfficeInfo"
                    class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium border rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 border-slate-200 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
                  >
                    {{ tr("office_btn_reload") }}
                  </button>
                </div>

                <p class="text-[11px] text-slate-400">
                  {{ tr("office_note_optional") }}
                </p>
              </footer>
            </form>
          </section>
        </div>
        <Toast
          :show="toast.show"
          :type="toast.type"
          :message="toast.message"
          @update:show="toast.show = $event"
        />
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from "vue";
import { messages } from "@/i18n";
import AdminSidebar from "@/components/Auth/Sidebar.vue";
import AdminTopbar from "@/components/Auth/TopNav.vue";
import DocCard from "@/components/Auth/DocCard.vue";
import Toast from "@/components/Toast.vue";
import api from "@/services/api";
import { fileToPayload } from "@/services/files";

/* =======================
  i18n / language
======================= */
const lang = ref(
  (document.documentElement.lang || localStorage.getItem("app_lang") || "ar")
    .toLowerCase()
    .startsWith("ar")
    ? "ar"
    : "en"
);

const tr = (key) =>
  (messages?.[lang.value] && messages[lang.value][key]) || key;

let langObserver;
const onLangEvent = (e) => {
  const next = (e.detail || "ar").toLowerCase().startsWith("en") ? "en" : "ar";
  if (next !== lang.value) lang.value = next;
};

onMounted(() => {
  window.addEventListener("app:lang", onLangEvent, { passive: true });

  langObserver = new MutationObserver((muts) => {
    if (
      muts?.length &&
      (muts[0].attributeName === "lang" || muts[0].attributeName === "dir")
    ) {
      const htmlLang = (
        document.documentElement.lang ||
        localStorage.getItem("app_lang") ||
        "ar"
      ).toLowerCase();
      const next = htmlLang.startsWith("en") ? "en" : "ar";
      if (next !== lang.value) lang.value = next;
    }
  });

  langObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["lang", "dir"],
  });
});

onBeforeUnmount(() => {
  if (langObserver) langObserver.disconnect();
  window.removeEventListener("app:lang", onLangEvent);
});

/* =======================
  Toast state + helper
======================= */
const toast = reactive({
  show: false,
  type: "success",
  message: "",
});

let toastTimer = null;
const showToast = (message, type = "success") => {
  toast.message = message;
  toast.type = type;
  toast.show = true;

  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => (toast.show = false), 2500);
};

/* =======================
  layout state + nav
======================= */
const isSidebarCollapsed = ref(false);
const isSidebarOpenMobile = ref(false);
const sidebarSearch = ref("");

const navItems = [
  {
    id: "account",
    icon: "mdi mdi-account-outline",
    labelKey: "sidebar_group_account",
    children: [
      {
        id: "account-settings",
        icon: "mdi mdi-tune-variant",
        labelKey: "sidebar_item_account_settings",
        route: "/account/settings/office",
      },
      {
        id: "office-management",
        icon: "mdi mdi-office-building-marker-outline",
        labelKey: "sidebar_item_office_management",
        route: "/offices",
      },
    ],
  },
  {
    id: "consultations",
    icon: "mdi mdi-forum-outline",
    labelKey: "sidebar_group_consultations",
    children: [
      {
        id: "consultation-create",
        icon: "mdi mdi-plus-circle-outline",
        labelKey: "sidebar_item_create",
        route: "/consultations/create",
      },
      {
        id: "consultation-my",
        icon: "mdi mdi-clipboard-text-outline",
        labelKey: "sidebar_item_my_consultations",
        route: "/consultations/my",
      },
    ],
  },
];

const toggleSidebar = () => {
  if (window.innerWidth < 768) {
    isSidebarOpenMobile.value = !isSidebarOpenMobile.value;
  } else {
    isSidebarCollapsed.value = !isSidebarCollapsed.value;
  }
};

/* =======================
  API config
======================= */
// =======================
// API config (dynamic office id)
// =======================

// اقرأ auth_user من الكوكي
const getCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const rawUser = getCookie("auth_user");
let authUser = null;
try {
  authUser = rawUser ? JSON.parse(rawUser) : null;
} catch (e) {
  authUser = null;
}

// حاول تطلع office_id من أي مسار محتمل
const OFFICE_ID =
  authUser?.office_id ||
  authUser?.office?.id ||
  authUser?.data?.office_id ||
  authUser?.data?.office?.id ||
  null;
console.log("user auth from cookie", authUser);
const API_SHOW = OFFICE_ID ? `/offices/${OFFICE_ID}` : null;
const API_SAVE = "/offices";
const submitting = ref(false);

/* =======================
  helper: normalize URL
  (relative -> full)
======================= */
const toFullUrl = (url) => {
  if (!url || typeof url !== "string") return null;
  if (url.startsWith("http")) return url;

  // ensure it starts with /
  const safe = url.startsWith("/") ? url : `/${url}`;

  // api.defaults.baseURL = https://domain.com/api/v1
  const base = (api.defaults.baseURL || "").replace(/\/api\/v1\/?$/, "");
  return base + safe;
};

/* =======================
  office form + uploads
======================= */
const officeForm = reactive({
  crn: "",
  establishment_no: "",
  unified_no: "",
  tax_no: "",
  insurance_no: "",
});

const docTypes = [
  {
    key: "logo",
    labelKey: "office_existing_logo",
    placeholderKey: "office_existing_logo",
  },
  {
    key: "letterhead",
    labelKey: "office_existing_letterhead",
    placeholderKey: "office_existing_letterhead",
  },
  {
    key: "unified_doc",
    labelKey: "office_existing_unified",
    placeholderKey: "office_existing_unified",
  },
  {
    key: "establishment_doc",
    labelKey: "office_existing_establishment",
    placeholderKey: "office_existing_establishment",
  },
  {
    key: "registeration_doc",
    labelKey: "office_existing_registration",
    placeholderKey: "office_existing_registration",
  },
];

const uploadedDocs = reactive({
  logo: null,
  letterhead: null,
  unified_doc: null,
  establishment_doc: null,
  registeration_doc: null,
});
const uploadedDocNames = reactive({
  logo: null,
  letterhead: null,
  unified_doc: null,
  establishment_doc: null,
  registeration_doc: null,
});

const uploadedDocTypes = reactive({
  logo: null,
  letterhead: null,
  unified_doc: null,
  establishment_doc: null,
  registeration_doc: null,
});

const selectedDocType = ref(docTypes[0].key);
const singleFileInput = ref(null);

const selectedDocMeta = computed(
  () => docTypes.find((d) => d.key === selectedDocType.value) || docTypes[0]
);

const hasAnyUploaded = computed(() =>
  Object.values(uploadedDocs).some(Boolean)
);

const triggerFilePicker = () => singleFileInput.value?.click();

const handleSelectedDocFile = async (e) => {
  const file = e.target.files?.[0] || null;
  if (file) {
    const payload = await fileToPayload(file); // {base64,name,type,size}
    uploadedDocs[selectedDocType.value] = payload;

    uploadedDocNames[selectedDocType.value] = payload.name;
    uploadedDocTypes[selectedDocType.value] = payload.type;
  }
  e.target.value = "";
};

const selectTypeAndPick = (key) => {
  selectedDocType.value = key;
  triggerFilePicker();
};

const removeUploaded = (key) => {
  uploadedDocs[key] = null;
  uploadedDocNames[key] = null;
  uploadedDocTypes[key] = null;
};

/* =======================
  existing docs URLs
======================= */
const existingFiles = reactive({
  logo_url: null,
  letterhead_url: null,
  unified_doc_url: null,
  establishment_doc_url: null,
  registeration_doc_url: null,
});

const existingFilesList = ref([]);

const openExistingFile = (key) => {
  const url = existingFiles[key];
  if (url) window.open(url, "_blank");
};

/* =======================
  hydrate + fetch
======================= */
const pick = (...vals) => vals.find((v) => v && v !== "null" && v !== "");

const mapFromArray = (arr) => {
  if (!Array.isArray(arr)) return {};
  const out = {};
  arr.forEach((it) => {
    const k = it.key || it.type || it.name;
    const u = it.url || it.path || it.file_url;
    if (k && u) out[k] = u;
  });
  return out;
};

const hydrateFromResponse = (payload) => {
  const d = payload?.data ?? payload ?? {};

  officeForm.crn = d.commercial_registration_number ?? d.crn ?? "";
  officeForm.establishment_no =
    d.establishment_number ?? d.establishment_no ?? "";
  officeForm.unified_no = d.unified_number ?? d.unified_no ?? "";
  officeForm.tax_no = d.tax_number ?? d.tax_no ?? "";
  officeForm.insurance_no = d.insurance_number ?? d.insurance_no ?? "";

  // ✅ sometimes GET returns attachments array
  const fromArr = mapFromArray(d.attachments || d.documents || d.files);

  existingFiles.logo_url = toFullUrl(
    pick(
      d.logo_url,
      d.logo,
      d.logo_path,
      d.logo_file,
      fromArr.logo,
      fromArr.logo_url
    )
  );

  existingFiles.letterhead_url = toFullUrl(
    pick(
      d.letterhead_url,
      d.letterhead,
      d.letterhead_path,
      fromArr.letterhead,
      fromArr.letterhead_url
    )
  );

  existingFiles.unified_doc_url = toFullUrl(
    pick(
      d.unified_doc_url,
      d.unified_doc,
      d.unified_doc_path,
      fromArr.unified_doc,
      fromArr.unified_doc_url
    )
  );

  existingFiles.establishment_doc_url = toFullUrl(
    pick(
      d.establishment_doc_url,
      d.establishment_doc,
      d.establishment_doc_path,
      fromArr.establishment_doc,
      fromArr.establishment_doc_url
    )
  );

  existingFiles.registeration_doc_url = toFullUrl(
    pick(
      d.registeration_doc_url,
      d.registeration_doc,
      fromArr.registeration_doc,
      fromArr.registeration_doc_url
    )
  );
  existingFilesList.value = [
    {
      key: "logo_url",
      labelKey: "office_existing_logo",
      url: existingFiles.logo_url,
    },
    {
      key: "letterhead_url",
      labelKey: "office_existing_letterhead",
      url: existingFiles.letterhead_url,
    },
    {
      key: "unified_doc_url",
      labelKey: "office_existing_unified",
      url: existingFiles.unified_doc_url,
    },
    {
      key: "establishment_doc_url",
      labelKey: "office_existing_establishment",
      url: existingFiles.establishment_doc_url,
    },
    {
      key: "registeration_doc_url",
      labelKey: "office_existing_registration",
      url: existingFiles.registeration_doc_url,
    },
  ].filter((x) => x.url && x.url !== "null" && x.url !== "");
};

const fetchOfficeInfo = async () => {
  if (!API_SHOW) return;
  try {
    const { data: res } = await api.get(API_SHOW, {
      headers: { Accept: "application/json" },
    });

    hydrateFromResponse(res);
  } catch (e) {
    console.error("fetchOfficeInfo error:", e);
    showToast(
      tr("office_fetch_failed") || "Failed to load office info",
      "error"
    );
  }
};

onMounted(fetchOfficeInfo);

/* =======================
  build JSON body & save
======================= */
const buildBody = () => ({
  commercial_registration_number: officeForm.crn || null,
  establishment_number: officeForm.establishment_no || null,
  unified_number: officeForm.unified_no || null,
  tax_number: officeForm.tax_no || null,
  insurance_number: officeForm.insurance_no || null,

  logo: uploadedDocs.logo || null,
  letterhead: uploadedDocs.letterhead || null,
  unified_doc: uploadedDocs.unified_doc || null,
  establishment_doc: uploadedDocs.establishment_doc || null,
  registeration_doc: uploadedDocs.registeration_doc || null,
});

console.log("OFFICE_ID:", OFFICE_ID);

// const buildFormData = () => {
//   const fd = new FormData();

//   fd.append("commercial_registration_number", officeForm.crn || "");
//   fd.append("establishment_number", officeForm.establishment_no || "");
//   fd.append("unified_number", officeForm.unified_no || "");
//   fd.append("tax_number", officeForm.tax_no || "");
//   fd.append("insurance_number", officeForm.insurance_no || "");

//   if (uploadedDocs.logo) fd.append("logo", uploadedDocs.logo);
//   if (uploadedDocs.letterhead) fd.append("letterhead", uploadedDocs.letterhead);
//   if (uploadedDocs.unified_doc) fd.append("unified_doc", uploadedDocs.unified_doc);
//   if (uploadedDocs.establishment_doc) fd.append("establishment_doc", uploadedDocs.establishment_doc);

//   // جرّب واحدة فقط حسب API:
//   if (uploadedDocs.registeration_doc) fd.append("registeration_doc", uploadedDocs.registeration_doc);
//   // أو:
//   // if (uploadedDocs.registeration_doc) fd.append("registeration_doc", uploadedDocs.registeration_doc);

//   return fd;
// };

const saveOfficeInfo = async () => {
  submitting.value = true;
  try {
    const body = buildBody();

    const { data: res } = await api.post(API_SAVE, body, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    await fetchOfficeInfo();
    Object.keys(uploadedDocs).forEach((k) => {
      uploadedDocs[k] = null;
      uploadedDocNames[k] = null;
      uploadedDocTypes[k] = null;
    });

    showToast(
      tr("office_saved_success") || "Office updated successfully",
      "success"
    );
  } catch (e) {
    const res = e.response?.data;
    const msg =
      res?.message ||
      Object.values(res?.errors || {})
        .flat()
        .join("\n") ||
      tr("office_saved_failed") ||
      "Failed to update office";
    showToast(msg, "error");
  } finally {
    submitting.value = false;
  }
};

const reloadOfficeInfo = () => fetchOfficeInfo();
</script>
